import React from "react";
import jsPDF from "jspdf";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { pdStory } from "../data/pdStory";

import {
    faDownload,
    faFlagCheckered,
    faArrowTrendUp,
    faMedal,
    faLightbulb,
    faCircleCheck,
} from "@fortawesome/free-solid-svg-icons";

const colorMap = {
    purple: {
        card: "bg-purple-900/20 border-purple-700",
        icon: "text-purple-400",
    },
    yellow: {
        card: "bg-yellow-900/20 border-yellow-700",
        icon: "text-yellow-400",
    },
    green: {
        card: "bg-green-900/20 border-green-700",
        icon: "text-green-400",
    },
    blue: {
        card: "bg-blue-900/20 border-blue-700",
        icon: "text-blue-400",
    },
    cyan: {
        card: "bg-cyan-900/20 border-cyan-700",
        icon: "text-cyan-400",
    },
    orange: {
        card: "bg-orange-900/20 border-orange-700",
        icon: "text-orange-400",
    },
    red: {
        card: "bg-red-900/20 border-red-700",
        icon: "text-red-400",
    },
};

const PDStoryboard = () => {

    const StoryCard = ({icon,title,content,color = "purple",}) => {
        const theme = colorMap[color] || colorMap.purple;
        return (
            <div
                className={`
                    ${theme.card}
                    border
                    rounded-3xl
                    p-6
                    backdrop-blur-md
                    shadow-xl
                    hover:scale-[1.01]
                    transition-all
                    duration-300
                `}
            >
                <div className="flex items-center gap-3 mb-5">
                    <FontAwesomeIcon
                        icon={icon}
                        className={`${theme.icon} text-xl`}
                    />

                    <h2 className="text-sm lg:text-lg font-bold text-white">
                        {title}
                    </h2>
                </div>

                <ul className="space-y-1">
                    {content.map((item, index) => (
                        <li
                            key={index}
                            className="flex gap-2 text-gray-300"
                        >
                            <FontAwesomeIcon
                                icon={faCircleCheck}
                                className="text-green-400 mt-1"
                            />
                            <span className="text-gray-100 text-xs lg:text-sm">{item}</span>
                        </li>
                    ))}
                </ul>
            </div>
        );
    };


    const handleDownloadPDF = () => {
        const pdf = new jsPDF("p", "mm", "a4");

        let y = 20;
        const addText = (text, size = 11) => {
            pdf.setFontSize(size);
            const lines = pdf.splitTextToSize(
                text,
                180
            );
            lines.forEach((line) => {
                if (y > 275) {
                    pdf.addPage();
                    y = 20;
                }

                pdf.text(line, 15, y);
                y += 7;
            });
        };

        pdf.setFontSize(20);
        pdf.text("Self BOS - 90 Day Transformation Storyboard",15,15);
        addText("A complete behavioral transformation journey powered by Self BOS.");

        pdStory.forEach((section) => {
            y += 5;
            if (y > 260) {
                pdf.addPage();
                y = 20;
            }
            pdf.setFontSize(15);
            pdf.text(section.title.toUpperCase(),15,y);
            y += 8;
            section.content.forEach((item) => {
                addText(`• ${item}`, 11);
            });
        });

        y += 10;
        addText("Small Actions → Daily Consistency → Behavioral Patterns → Self Awareness → Transformation → Better Version Of You",13);

        pdf.save("SelfBOS-90Day-Storyboard.pdf");
    };

    return (
        <div className="space-y-6 bg-gray-900 p-6 rounded-xl text-white border-t-2 border-cyan-200 mt-2">
            <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
                <div
                    className="
                        relative
                        overflow-hidden
                        rounded-3xl
                        bg-gradient-to-br
                        from-purple-950
                        via-indigo-950
                        to-black
                        border
                        border-purple-800
                        p-8
                        md:p-12
                        mb-8
                    "
                >
                    <div className="absolute inset-0 opacity-20 bg-[radial-gradient(circle_at_top_right,_#9333ea,_transparent_40%)]" />
                        <div className="relative z-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                            <div>
                                <div className="inline-flex items-center gap-2 bg-purple-900/40 border border-purple-700 rounded-full px-4 py-2 text-sm mb-4">
                                    <FontAwesomeIcon
                                        icon={faArrowTrendUp}
                                    />
                                    Self BOS Behavioral Transformation
                                </div>
                                <h1 className="text-start items-start text-sm lg:text-lg font-black leading-tight">
                                    90 Day
                                    <span className="block text-purple-400">
                                        Storyboard
                                    </span>
                                </h1>
                                <p className="text-gray-200 mt-2 text-xs lg:text-sm  max-w-3xl">
                                    Discover how a person transforms from Day 0 to Day 90 using small daily actions, behavioral
                                    tracking, weekly reviews, and continuous self correction.
                                </p>
                            </div>
                            <button
                                onClick={
                                    handleDownloadPDF
                                }
                                className="
                                    bg-green-600
                                    hover:bg-green-700
                                    px-2
                                    py-1
                                    w-fit
                                    text-xs
                                    rounded-2xl
                                    flex
                                    items-center
                                    gap-3
                                    font-semibold
                                    shadow-lg
                                    transition-all
                                "
                            >
                                <FontAwesomeIcon
                                    icon={faDownload}
                                />
                                Download PDF
                            </button>
                        </div>
                    </div>

                    {/* STORY CARDS */}
                    <div className="grid md:grid-cols-2 gap-2">
                        {pdStory.map((section) => (
                            <StoryCard
                                key={section.title}
                                icon={section.icon}
                                title={section.title}
                                content={
                                    section.content
                                }
                                color={
                                    section.color
                                }
                            />
                        ))}
                    </div>

                    {/* BEFORE VS AFTER */}
                    <div className="grid md:grid-cols-2 gap-2 mt-2">
                        <div className="bg-red-950/30 border border-red-800 rounded-3xl p-6">
                            <h2 className="text-sm lg:text-lg text-cyan-200 font-bold mb-5">
                                Day 0
                            </h2>
                            <ul className="space-y-3 text-xs lg:text-sm text-gray-300">
                                <li className="text-gray-100 text-xs lg:text-sm ">
                                    • Low energy
                                </li>
                                 <li className="text-gray-100 text-xs lg:text-sm ">
                                    • Inconsistent routines
                                </li>
                                 <li className="text-gray-100 text-xs lg:text-sm ">
                                    • Poor sleep
                                </li>
                                 <li className="text-gray-100 text-xs lg:text-sm ">
                                    • Weak focus
                                </li>
                                <li className="text-gray-100 text-xs lg:text-sm ">
                                    • No behavior awareness
                                </li>
                            </ul>
                        </div>

                        <div className="bg-green-950/30 border border-green-800 rounded-3xl p-2">
                            <h2 className="text-sm lg:text-lg text-cyan-200 font-bold mb-2">
                                After Day 90
                            </h2>

                            <ul className="space-y-3 text-xs lg:text-sm text-gray-300">
                                 <li className="text-gray-100 text-xs lg:text-sm ">
                                    • Better health
                                </li>
                                 <li className="text-gray-100 text-xs lg:text-sm ">
                                    • Higher focus
                                </li>
                                <li className="text-gray-100 text-xs lg:text-sm ">
                                    • Strong discipline
                                </li>
                                 <li className="text-gray-100 text-xs lg:text-sm ">
                                    • Better sleep
                                </li>
                                 <li className="text-gray-100 text-xs lg:text-sm ">
                                    • Improved self awareness
                                </li>
                            </ul>
                        </div>
                    </div>

                    {/* FINAL CTA */}
                    <div
                        className="
                            mt-2
                            rounded-3xl
                            overflow-hidden
                            bg-gradient-to-r
                            from-green-900
                            via-emerald-900
                            to-green-950
                            border
                            border-green-700
                        "
                    >
                        <div className="p-2 text-center">
                            <FontAwesomeIcon icon={faFlagCheckered} className="text-lg lg:text-xl text-green-300 mb-2"/>
                            <h2 className="text-sm lg:text-lg text-green-300 font-black mb-2">
                                Your Next 90 Days Can Change Your Next 10 Years
                            </h2>
                            <p className="text-gray-200 max-w-4xl mx-auto text-xs">
                                Small Actions → Daily Consistency → Behavioral Patterns → Self Awareness → Transformation → Better Version Of You
                            </p>
                            <div className="flex flex-wrap justify-center gap-2 mt-2">
                                <div className="bg-black/30 rounded-xl text-xs px-2 py-1">
                                    <FontAwesomeIcon icon={faLightbulb} className="mr-2 text-yellow-400"/>
                                    Awareness
                                </div>
                                <div className="bg-black/30 rounded-xl text-xs px-2 py-1">
                                    <FontAwesomeIcon icon={faArrowTrendUp} className="mr-2 text-cyan-400"/>
                                    Growth
                                </div>
                                <div className="bg-black/30 rounded-xl text-xs px-2 py-1">
                                    <FontAwesomeIcon icon={faMedal} className="mr-2 text-orange-400"/>
                                    Transformation
                                </div>
                            </div>
                        </div>
                    </div>
            </div>
        </div>
    );
};

export default PDStoryboard;
